import {
  CUSTOM_FIELD_OPERATIONS,
  PERM_MOD_AUTOMATIONS,
  PERM_MOD_CUSTOM_FIELDS,
} from 'src/constants';
import { useFeatureFlags } from 'src/context';
import { useAuditLogGate } from 'src/hooks/useAuditLogGate';
import { usePlanStatus } from 'src/hooks/usePlanStatusHook';
import { useAppSelector } from 'src/hooks/useStore';
import { useRouteAccess } from 'src/routes/routeAccess';
import { useGetPortalConfigQuery } from 'src/services/api';
import { hasOperation, hasPermission } from 'src/utils/UserUtils';

import type { SettingsVisibilityContext } from './settingsRegistry';

export const useSettingsVisibilityContext = ({
  shouldPreviewAsClient = false,
}: { shouldPreviewAsClient?: boolean } = {}): SettingsVisibilityContext => {
  const { routeAccess } = useRouteAccess({ shouldPreviewAsClient });
  const { isStarterPlan, isTrialing, requireProUpgrade, studio } =
    usePlanStatus();
  const { allowAuditLog } = useAuditLogGate();
  const { EnableAuditLogs, EnableNewCustomFields, EnableStudioSidebar } =
    useFeatureFlags();
  // Read synchronously rather than through useUserAccess, which reports false
  // for a render after permissions load and would flash a page as not allowed.
  const permissions = useAppSelector((state) => state.user.data?.permissions);
  const isClient = useAppSelector(
    (state) => state.user.isClient || shouldPreviewAsClient,
  );
  const { data: portalConfigurations } = useGetPortalConfigQuery();

  return {
    routeAccess,
    isClient,
    hasAutomationsAccess: hasPermission(PERM_MOD_AUTOMATIONS, permissions),
    canManageCustomFields: hasOperation(
      PERM_MOD_CUSTOM_FIELDS,
      CUSTOM_FIELD_OPERATIONS.CHANGE_TEMPLATE,
      permissions,
    ),
    hideWorkspacePagesFromClient:
      isClient && Boolean(portalConfigurations?.structFields?.disableCompanies),
    allowAuditLog,
    planStatus: { isStarterPlan, isTrialing, requireProUpgrade, studio },
    flags: { EnableAuditLogs, EnableNewCustomFields, EnableStudioSidebar },
  };
};
